<template>
  <BaseDialog
    :visible="props.visible"
    @update:visible="(val) => emit('update:visible', val)"
    class="!w-[95%] sm:!w-[90%] md:!w-[50%] !max-w-[900px] h-[80vh] sm:h-[85vh] md:h-[90vh] md:rounded-[16px] rounded-[12px]"
    height="auto"
    :draggable="false"
    margin="20px"
  >
    <div
      v-if="generatingCode"
      class="flex flex-col items-center justify-center gap-[10px] h-[270px]"
    >
      <h2
        class="font-just text-[32px] md:text-[32px] font-normal leading-[150%] tracking-[0%] align-middle text-btnActive mb-6 text-center"
      >
        {{ $t('setPrincipalLabels.generatingCodeText') }}
      </h2>
      <div class="flex items-center gap-2 w-[50%]">
        <ProgressBar
          :value="progress"
          :showValue="false"
          class="w-full"
          :pt="{
            root: {
              style:
                'height: 5.15px; border-radius: 9999px; background-color: #494949;',
            },
            value: {
              style:
                'background-image: linear-gradient(to right, #00CCFF, #05F4EC); border-radius: 9999px;',
            },
          }"
        />
        <div
          class="flex items-center justify-center"
          v-html="rightArrow({ width: 24, height: 24, color: '#1E1E1E' })"
        ></div>
      </div>
      <div
        class="gap-[24px] flex items-center justify-center mx-[24px] mt-[32px] mb-[12px] w-full"
      ></div>
      <div v-if="error">
        <span
          class="text-base my-2 !font-just text-error flex justify-center"
          data-test-id="errorMessage"
        >
          {{ error }}
        </span>
      </div>
    </div>
    <div v-else>
      <div class="flex justify-end items-center pt-4 pr-4">
        <button
          @click="emit('update:visible', false)"
          class="cursor-pointer border-none bg-transparent flex justify-center items-center"
          v-html="cancelSvg"
          :aria-label="$t('accessibilityLabels.close')"
          :autofocus="visible ? true : false"
        ></button>
      </div>
      <div class="flex justify-center pb-[32px]">
        <div
          class="z-0 bg-gradient-to-r rounded-full flex items-center justify-center from-warningBgGradient p-3 opacity-25 to-warningBgGradientVia"
          v-html="tooltip({ width: '48', height: '48', color: '#FFFFFF' })"
        ></div>
        <div
          class="z-10 bg-gradient-to-r rounded-full flex items-center justify-center from-mintGradientFrom to-mintGradientTo p-3 ml-[-10px]"
          v-html="
            accountAgentSvg({ width: '48', height: '48', color: '#FFFFFF' })
          "
        ></div>
      </div>

      <div class="flex items-center justify-center mb-[32px]">
        <div>
          <h2
            v-html="title"
            class="font-just px-4 sm:px-6 font-400 text-[20px] sm:text-[24px] leading-[100%] text-btnActive"
          ></h2>
        </div>
      </div>

      <div class="flex items-center justify-center mb-[32px] text-center">
        <p
          class="font-just px-4 sm:px-6 text-[16px] sm:text-[20px] leading-[100%] font-400"
          v-html="subtitle"
        ></p>
      </div>

      <div
        class="mx-4 sm:mx-[24px] bg-gradient-to-l rounded-[16px] from-agentGradientVia to-agentGradient z-0"
      >
        <div class="px-4 sm:px-[32px] py-[24px]">
          <BaseInputOtp
            class="w-full flex justify-center"
            v-model="form.token"
            :name="'otp'"
            id="otp"
            :integerOnly="true"
            :length="otpLength"
          />
          <div class="flex items-center justify-center pt-[24px]">
            <p
              class="font-just font-400 text-[20px] leading-[100%]"
              v-html="$t('setPrincipalLabels.codeValidTime', { time: expireTime || '00:00' })"
            ></p>
          </div>
        </div>
      </div>

      <div class="flex justify-center w-full pt-[32px] px-4 sm:px-[24px]">
        <BaseButton
          :label="$t('commonLabels.done')"
          class="w-full lg:w-[280px] lg:px-0"
          @click="handleSubmit"
        />
      </div>

      <div class="flex items-center justify-center my-[20px]">
         <p
          @click="isExpired ? handleGenerateNewCode() : null"
          :class="[
            'font-just font-400 text-[16px] leading-[24px] tracking-[-0.2%]',
            isExpired 
              ? 'text-lightGrey cursor-pointer' 
              : 'text-gray-400 cursor-not-allowed opacity-50'
          ]"
        >
          {{ $t('setPrincipalLabels.generateNewCode') }}
        </p>
      </div>
    </div>
  </BaseDialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import {
  closeSvg,
  tooltip,
  accountAgentSvg,
  rightArrow,
} from "@/utils/commonSvg";

const props = defineProps({
  generatingCode: Boolean,
  progress: Number,
  visible: {
    type: Boolean,
    default: true,
  },
  showCancel: {
    type: Boolean,
    default: true,
  },
  item: { required: true },
  code: { 
    type: [String, Number],
    default: ""
  },
  error: {},
  expireTime: {},
  title: {},
  subtitle: {},
  otpLength: {
    type: Number,
    default: 6
  }
});

const fullscreen = ref(true);
const form = ref({
  token: "",
});

const isExpired = computed(() => {
  if (!props.expireTime) return false;
  const timeStr = String(props.expireTime).trim();
  // Check for various formats of zero time
  return timeStr === "00:00" || timeStr === "0:00" || timeStr === "00:0" || timeStr === "0:0";
});

// Watch for code changes and update form.token
watch(() => props.code, (newCode) => {
  if (newCode) {
    form.value.token = String(newCode);
  }
}, { immediate: true });

const emit = defineEmits([
  "update:visible",
  "codeGenerated",
  "generateNewCode",
]);

const validate = ref(false);

watch(validate, (newVal) => {
  if (newVal) {
    emit('update:visible', false);
  }
});

const cancelSvg = computed(() =>
  closeSvg({
    height: fullscreen.value ? "32px" : "24px",
    width: fullscreen.value ? "32px" : "24px",
  })
);

const expireTime = computed(() => props.expireTime || "00:00");

const handleSubmit = () => {
  emit("codeGenerated");
};

const handleGenerateNewCode = () => {
  emit("generateNewCode");
};
</script>